<template>
  <div
    class="vacancy-wrapper"
    draggable="true"
    @dragstart="onDragStart"
    @click="$emit('click')"
  >
    <article
      class="vacancy-card"
      :class="{
        published: vacancy.is_public === true,
        archived: vacancy.is_public !== true
      }"
    >
      <div class="vacancy-card-top">
        <div class="vacancy-status">
          <span class="status-dot"></span>
        </div>

        <span class="vacancy-date">
          {{ formattedDate }}
        </span>
      </div>

      <div class="vacancy-header">
        <h3 class="vacancy-title">
          {{ vacancy.title || "Без названия" }}
        </h3>

        <div
          class="vacancy-responses"
          :class="{
            active: vacancy.response_count > 0
          }"
        >
          <span class="responses-value">
            {{ vacancy.response_count || 0 }}
          </span>

          <span>
            {{ responsesLabel }}
          </span>
        </div>
      </div>

      <p class="vacancy-description">
        {{ vacancy.info || "Описание вакансии не добавлено" }}
      </p>

      <div class="card-arrow">
        <img
          :src="arrowIcon"
          alt=""
        />
      </div>
    </article>

    <div class="vacancy-bottom">

<div class="vacancy-actions">

  <button
    type="button"
    class="edit-block"
    @click.stop="$emit('edit', vacancy)"
  >
    <span class="edit-icon-wrapper">
      <img
        :src="editIcon"
        class="edit-icon"
        alt=""
      />
    </span>

    <span>
      Редактировать
    </span>
  </button>


  <span class="actions-divider"></span>


  <button
    type="button"
    class="delete-block"
    @click.stop="$emit('delete', vacancy)"
  >

    <span class="delete-icon-wrapper">

      <svg
        viewBox="0 0 20 20"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M4.5 6H15.5"
          stroke="currentColor"
          stroke-width="1.45"
          stroke-linecap="round"
        />

        <path
          d="M8 3.8H12"
          stroke="currentColor"
          stroke-width="1.45"
          stroke-linecap="round"
        />

        <path
          d="M6 6L6.7 15.1C6.76 15.9 7.42 16.5 8.22 16.5H11.78C12.58 16.5 13.24 15.9 13.3 15.1L14 6"
          stroke="currentColor"
          stroke-width="1.45"
          stroke-linecap="round"
        />

        <path
          d="M8.5 9V13.5M11.5 9V13.5"
          stroke="currentColor"
          stroke-width="1.35"
          stroke-linecap="round"
        />
      </svg>

    </span>

    <span>
      Удалить
    </span>

  </button>

</div>


<div class="views-block">

  <img
    :src="eyeIcon"
    class="eye-icon"
    alt=""
  />

  <span>
    {{ viewsText }}
  </span>

</div>

</div>



  </div>
</template>

<script setup>
import { computed } from "vue";

const props = defineProps({
  vacancy: {
    type: Object,
    required: true
  }
});

defineEmits([
  "click",
  "edit",
  "delete"
]);

const eyeIcon = new URL(
  "../assets/eye-icon.png",
  import.meta.url
).href;

const arrowIcon = new URL(
  "../assets/arrow-icon.png",
  import.meta.url
).href;

const editIcon = new URL(
  "../assets/edit-vacancy.png",
  import.meta.url
).href;

const onDragStart = event => {
  const vacancyId =
    props.vacancy.id ||
    props.vacancy._id;

  if (!vacancyId) {
    console.warn(
      "Не удалось определить ID вакансии",
      props.vacancy
    );

    event.preventDefault();
    return;
  }

  event.dataTransfer.setData(
    "vacancy_id",
    String(vacancyId)
  );

  event.dataTransfer.effectAllowed = "move";
};

const formattedDate = computed(() => {
  const rawValue =
    Number(props.vacancy.date_of_create);

  if (!Number.isFinite(rawValue)) {
    return "Дата не указана";
  }

  /*
    Поддерживаем timestamp как в секундах,
    так и в миллисекундах.
  */
  const timestamp =
    rawValue < 10_000_000_000
      ? rawValue * 1000
      : rawValue;

  const difference =
    Date.now() - timestamp;

  const daysAgo = Math.floor(
    difference / 86_400_000
  );

  if (daysAgo <= 0) {
    return "Сегодня";
  }

  if (daysAgo === 1) {
    return "Вчера";
  }

  if (daysAgo < 5) {
    return `${daysAgo} дня назад`;
  }

  return `${daysAgo} дней назад`;
});

const responsesLabel = computed(() => {
  const count =
    Number(props.vacancy.response_count) || 0;

  const lastDigit = count % 10;
  const lastTwoDigits = count % 100;

  if (
    lastDigit === 1 &&
    lastTwoDigits !== 11
  ) {
    return "отклик";
  }

  if (
    [2, 3, 4].includes(lastDigit) &&
    ![12, 13, 14].includes(lastTwoDigits)
  ) {
    return "отклика";
  }

  return "откликов";
});

const viewsText = computed(() => {
  const count =
    Number(props.vacancy.views) || 0;

  const lastDigit = count % 10;
  const lastTwoDigits = count % 100;

  if (
    lastDigit === 1 &&
    lastTwoDigits !== 11
  ) {
    return `${count} просмотр`;
  }

  if (
    [2, 3, 4].includes(lastDigit) &&
    ![12, 13, 14].includes(lastTwoDigits)
  ) {
    return `${count} просмотра`;
  }

  return `${count} просмотров`;
});
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.vacancy-wrapper {
  width: 100%;
  max-width: 100%;

  display: flex;
  flex-direction: column;
  gap: 10px;

  cursor: grab;

  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.vacancy-wrapper:active {
  cursor: grabbing;
  opacity: 0.76;
  transform: scale(0.985);
}

/* Card */

.vacancy-card {
  position: relative;

  width: 100%;
  min-height: 175px;

  display: flex;
  flex-direction: column;

  padding: 18px 18px 17px;

  overflow: hidden;
  cursor: pointer;

  background:
    radial-gradient(
      circle at 100% 0%,
      rgba(65, 133, 245, 0.075),
      transparent 145px
    ),
    var(--my-vacancy-card-bg);

  border: 1px solid
    rgba(40, 70, 110, 0.075);

  border-radius: 20px;

  box-shadow:
    0 1px 2px
    rgba(15, 23, 42, 0.025),
    0 8px 24px
    rgba(15, 23, 42, 0.045);

  transition:
    transform 0.24s ease,
    border-color 0.24s ease,
    box-shadow 0.24s ease;
}

.vacancy-card::before {
  content: "";

  position: absolute;
  top: 0;
  left: 22px;
  right: 22px;

  height: 2px;

  background:
    linear-gradient(
      90deg,
      transparent,
      rgba(53, 132, 247, 0.6),
      transparent
    );

  opacity: 0;

  transition:
    opacity 0.24s ease;
}

.vacancy-card:hover {
  transform: translateY(-3px);

  border-color:
    rgba(53, 132, 247, 0.18);

  box-shadow:
    0 2px 4px
    rgba(15, 23, 42, 0.025),
    0 15px 34px
    rgba(35, 84, 160, 0.1);
}

.vacancy-card:hover::before {
  opacity: 1;
}

/* Top row */

.vacancy-card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  margin-bottom: 14px;
}

.vacancy-status {
  display: inline-flex;
  align-items: center;
  gap: 7px;

  padding: 5px 9px;

  color: #23895a;
  background: rgba(39, 168, 99, 0.085);

  border: 1px solid
    rgba(39, 168, 99, 0.11);

  border-radius: 999px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;
  line-height: 1;
}

.status-dot {
  width: 6px;
  height: 6px;

  flex-shrink: 0;

  background: #2ca96a;
  border-radius: 50%;

  box-shadow:
    0 0 0 4px
    rgba(44, 169, 106, 0.11);
}

.vacancy-card.archived
.vacancy-status {
  color: #888f9b;
  background: rgba(120, 128, 141, 0.08);
  border-color: rgba(120, 128, 141, 0.1);
}

.vacancy-card.archived
.status-dot {
  background: #949ba6;

  box-shadow:
    0 0 0 4px
    rgba(148, 155, 166, 0.1);
}

.vacancy-date {
  flex-shrink: 0;

  color: #969ca7;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 10px;
}

/* Header */

.vacancy-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
}

.vacancy-title {
  min-width: 0;
  flex: 1;

  display: -webkit-box;

  margin: 0;

  overflow: hidden;

  color: var(--text-primary);

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 16px;
  line-height: 1.3;

  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.vacancy-responses {
  min-width: 66px;

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;

  flex-shrink: 0;

  padding: 6px 9px;

  color: #9399a4;
  background: rgba(120, 128, 141, 0.075);

  border: 1px solid
    rgba(120, 128, 141, 0.09);

  border-radius: 10px;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 9px;
  white-space: nowrap;
}

.vacancy-responses.active {
  color: #347cd9;
  background: rgba(52, 132, 247, 0.08);
  border-color: rgba(52, 132, 247, 0.12);
}

.responses-value {
  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 11px;
}

/* Description */

.vacancy-description {
  max-width: calc(100% - 34px);

  display: -webkit-box;

  margin: 12px 0 0;

  overflow: hidden;

  color: var(--text-secondary);

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 12px;
  line-height: 1.55;

  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

/* Arrow */

.card-arrow {
  position: absolute;
  right: 15px;
  bottom: 17px;

  width: 27px;
  height: 27px;

  display: flex;
  align-items: center;
  justify-content: center;

  background: rgba(100, 110, 125, 0.075);
  border-radius: 9px;

  transition:
    background-color 0.22s ease,
    transform 0.22s ease;
}

.card-arrow img {
  width: 5px;
  height: 10px;

  opacity: 0.45;

  transition:
    opacity 0.22s ease,
    transform 0.22s ease;
}

.vacancy-card:hover
.card-arrow {
  background: rgba(53, 132, 247, 0.1);
  transform: translateX(2px);
}

.vacancy-card:hover
.card-arrow img {
  opacity: 0.8;
  transform: translateX(1px);
}

/* Bottom bar */

.vacancy-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;

  min-height: 38px;

  padding: 0 7px;
}

.edit-block {
  display: inline-flex;
  align-items: center;
  gap: 7px;

  padding: 5px 8px 5px 5px;

  color: #a85159;
  background: transparent;

  border: none;
  border-radius: 10px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 11px;

  cursor: pointer;

  transition:
    color 0.2s ease,
    background-color 0.2s ease,
    transform 0.2s ease;
}

.edit-block:hover {
  color: #c84c57;
  background: rgba(213, 78, 88, 0.07);

  transform: translateX(2px);
}

.edit-icon-wrapper {
  width: 27px;
  height: 27px;

  display: flex;
  align-items: center;
  justify-content: center;

  background: rgba(201, 76, 87, 0.075);
  border-radius: 9px;
}

.edit-icon {
  width: 18px;
  height: 18px;

  object-fit: contain;

  opacity: 0.78;

  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.edit-block:hover
.edit-icon {
  opacity: 1;
  transform: rotate(-4deg) scale(1.05);
}

.views-block {
  display: inline-flex;
  align-items: center;
  gap: 6px;

  color: #838c9d;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 10px;
}

.eye-icon {
  width: 13px;
  height: 13px;

  object-fit: contain;

  opacity: 0.65;
}

/* Dark theme */

:global(html[data-theme="dark"])
.vacancy-card {
  background:
    radial-gradient(
      circle at 100% 0%,
      rgba(54, 132, 255, 0.085),
      transparent 150px
    ),
    #1c1c1e;

  border-color:
    rgba(255, 255, 255, 0.07);

  box-shadow:
    0 10px 28px
    rgba(0, 0, 0, 0.13);
}

:global(html[data-theme="dark"])
.vacancy-card:hover {
  border-color:
    rgba(80, 145, 245, 0.18);

  box-shadow:
    0 16px 38px
    rgba(0, 0, 0, 0.24);
}

:global(html[data-theme="dark"])
.vacancy-title {
  color: #f1f2f4;
}

:global(html[data-theme="dark"])
.vacancy-description {
  color: #989da7;
}

:global(html[data-theme="dark"])
.vacancy-date {
  color: #727780;
}

:global(html[data-theme="dark"])
.vacancy-responses {
  color: #8b919c;
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.06);
}

:global(html[data-theme="dark"])
.vacancy-responses.active {
  color: #79aaf3;
  background: rgba(63, 137, 241, 0.09);
  border-color: rgba(74, 145, 246, 0.13);
}

:global(html[data-theme="dark"])
.card-arrow {
  background: rgba(255, 255, 255, 0.055);
}

:global(html[data-theme="dark"])
.edit-block {
  color: #c97c82;
}

:global(html[data-theme="dark"])
.edit-block:hover {
  color: #f08b93;
  background: rgba(218, 84, 95, 0.1);
}

:global(html[data-theme="dark"])
.edit-icon-wrapper {
  background: rgba(218, 84, 95, 0.1);
}

:global(html[data-theme="dark"])
.views-block {
  color: #858b96;
}

/* Mobile */

@media (max-width: 430px) {
  .vacancy-wrapper {
    gap: 8px;
  }

  .vacancy-card {
    min-height: 168px;

    padding: 16px;

    border-radius: 18px;
  }

  .vacancy-card-top {
    margin-bottom: 12px;
  }

  .vacancy-status {
    padding: 5px 8px;
    font-size: 8px;
  }

  .vacancy-date {
    font-size: 9px;
  }

  .vacancy-header {
    gap: 9px;
  }

  .vacancy-title {
    font-size: 14px;
  }

  .vacancy-responses {
    min-width: auto;

    padding: 5px 7px;

    font-size: 8px;
  }

  .responses-value {
    font-size: 10px;
  }

  .vacancy-description {
    margin-top: 10px;

    font-size: 11px;
    line-height: 1.5;
  }

  .card-arrow {
    right: 13px;
    bottom: 14px;

    width: 25px;
    height: 25px;
  }

  .vacancy-bottom {
    padding: 0 3px;
  }

  .edit-block {
    padding-left: 3px;
    font-size: 10px;
  }

  .edit-icon-wrapper {
    width: 25px;
    height: 25px;
  }

  .edit-icon {
    width: 16px;
    height: 16px;
  }

  .views-block {
    font-size: 10px;
  }

  .vacancy-card:hover {
    transform: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .vacancy-wrapper,
  .vacancy-card,
  .card-arrow,
  .card-arrow img,
  .edit-block,
  .edit-icon {
    transition: none;
  }
}

.vacancy-actions {
  display: inline-flex;

  align-items: center;

  gap: 3px;

  min-width: 0;
}


.actions-divider {
  width: 1px;

  height: 19px;

  margin:
    0
    3px;

  background:
    rgba(110, 120, 135, .11);

  border-radius:
    999px;
}


/* ============================================================
   DELETE
============================================================ */

.delete-block {
  display: inline-flex;

  align-items: center;

  gap: 7px;

  padding:
    5px
    8px
    5px
    5px;

  color:
    #a76b70;

  background:
    transparent;

  border:
    none;

  border-radius:
    10px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    11px;

  cursor:
    pointer;

  transition:
    color .18s ease,
    background-color .18s ease,
    transform .18s ease;
}


.delete-block:hover {
  color:
    #d4545e;

  background:
    rgba(213, 78, 88, .07);

  transform:
    translateX(2px);
}


.delete-icon-wrapper {
  width:
    27px;

  height:
    27px;

  flex:
    0
    0
    27px;

  display:
    flex;

  align-items:
    center;

  justify-content:
    center;

  color:
    #b26870;

  background:
    rgba(201, 76, 87, .065);

  border-radius:
    9px;

  transition:
    color .18s ease,
    background-color .18s ease,
    transform .18s ease;
}


.delete-icon-wrapper svg {
  width:
    17px;

  height:
    17px;
}


.delete-block:hover
.delete-icon-wrapper {
  color:
    #d4545e;

  background:
    rgba(213, 78, 88, .10);

  transform:
    scale(1.04);
}

@media (max-width: 430px) {

.vacancy-actions {
  gap:
    1px;
}


.actions-divider {
  margin:
    0
    1px;
}


.delete-block {
  padding-left:
    3px;

  font-size:
    10px;
}


.delete-icon-wrapper {
  width:
    25px;

  height:
    25px;

  flex-basis:
    25px;
}


.delete-icon-wrapper svg {
  width:
    16px;

  height:
    16px;
}

}

@media (prefers-reduced-motion: reduce) {

.delete-block,
.delete-icon-wrapper {
  transition:
    none;
}

}

</style>